/**
 * ONE source census for every headline count.
 *
 * Before this module each landscape counted its own way (risk-overview rows minus landscape=none;
 * every connection minus s3/sftp/dynamodb; actionable connections minus dynamodb …) and a demo
 * showed 89, 88 and 62 "sources" on three consecutive screens. The rules below are the shared
 * definition; a screen that needs a narrower set (mask-eligible, with an inventory …) applies
 * that AFTER the census and says so on screen ("N of T sources").
 *
 * Visible by default = every data source EXCEPT
 *   - rows tagged landscape=none (TDM staging sources and raw views, anything a user hid),
 *   - platform-managed rows (`ownerRef` set — the same TDM rows, kept as a separate guard),
 *   - sub-sources (`parentDataSourceId` set — "Discover everything" fan-out children, which
 *     belong to their parent's card and would otherwise inflate the estate count).
 *
 * `rows` may be risk-overview rows (which carry no ownerRef / parentDataSourceId) or the
 * connections themselves; the connection with the same id is joined in either way.
 */

const idKey = (x) => (x == null ? '' : String(x));

export const isSubSource = (conn) => Boolean(conn && conn.parentDataSourceId != null);

const isHidden = (row, conn) => {
  const fromRow = row && row.landscape != null ? String(row.landscape).toLowerCase() : null;
  const fromConn = conn && conn.tags && conn.tags.landscape != null ? String(conn.tags.landscape).toLowerCase() : null;
  return fromRow === 'none' || fromConn === 'none';
};

export function buildCensus(rows, connections, options = {}) {
  const { includePlatformManaged = false, includeSubSources = false, includeHidden = false } = options;
  const byId = new Map();
  (connections || []).forEach((c) => {
    if (c && c.id != null) byId.set(idKey(c.id), c);
  });
  const visible = [];
  const excluded = { hidden: [], platformManaged: [], subSources: [] };
  (rows || []).forEach((row) => {
    if (!row) return;
    const conn = byId.get(idKey(row.id)) || null;
    if (!includeHidden && isHidden(row, conn)) {
      excluded.hidden.push(row);
      return;
    }
    if (!includePlatformManaged && ((conn && conn.ownerRef) || row.ownerRef)) {
      excluded.platformManaged.push(row);
      return;
    }
    if (!includeSubSources && (isSubSource(conn) || isSubSource(row))) {
      excluded.subSources.push(row);
      return;
    }
    visible.push(conn && conn !== row ? { ...row, connection: conn } : row);
  });
  return { visible, excluded };
}

/** Has this source EVER completed a discovery? (not windowed — see discoveredWithin). */
export const isDiscovered = (row) =>
  Boolean(row) && (row.latestDiscoveryAt != null || row.latestDiscoveryId != null);

/**
 * The ONE pipeline bucketing rule (was PipelineBoard.stageOf; the Workflow header funnel used a
 * slightly different one and the two never summed). 'secured' | 'discovered' | 'profiled' |
 * 'surfaced'. A source with no history counts as profiled once its connection test passed.
 */
export const sourceStage = (row, { maskStatusById = {}, profiledIds = new Set(), testResultById = {} } = {}) => {
  const maskStatus = maskStatusById[row.id];
  if (maskStatus === 'MASKED' || maskStatus === 'MASKING') return 'secured';
  if (isDiscovered(row)) return 'discovered';
  if (profiledIds.has(Number(row.id))) return 'profiled';
  return testResultById[String(row.id)] === true ? 'profiled' : 'surfaced';
};

export function censusCounts(visible, ctx) {
  const counts = { total: 0, secured: 0, discovered: 0, profiled: 0, surfaced: 0, everDiscovered: 0, neverDiscovered: 0 };
  (visible || []).forEach((row) => {
    counts.total += 1;
    counts[sourceStage(row, ctx)] += 1;
    if (isDiscovered(row)) counts.everDiscovered += 1;
  });
  counts.neverDiscovered = counts.total - counts.everDiscovered;
  return counts;
}

/** Sources whose latest discovery finished inside the trailing window. Always label it "last N days". */
export function discoveredWithin(visible, days, now = Date.now()) {
  const cutoff = now - days * 86400000;
  return (visible || []).filter((row) => {
    const t = row && row.latestDiscoveryAt ? Date.parse(row.latestDiscoveryAt) : NaN;
    return Number.isFinite(t) && t >= cutoff;
  }).length;
}

const fmt = (n) => Number(n || 0).toLocaleString();

/** "89 sources · 62 discovered · 27 never discovered" — the same sentence on every header. */
export function censusLabel(counts) {
  const c = counts || {};
  const total = Number(c.total || 0);
  return `${fmt(total)} source${total === 1 ? '' : 's'} · ${fmt(c.everDiscovered)} discovered · ${fmt(c.neverDiscovered)} never discovered`;
}

/** "3 sub-sources and 2 platform-managed rows are not counted" — or '' when nothing was excluded. */
export function excludedNote(excluded) {
  const e = excluded || {};
  const parts = [];
  if ((e.subSources || []).length) parts.push(`${fmt(e.subSources.length)} sub-source${e.subSources.length === 1 ? '' : 's'}`);
  if ((e.platformManaged || []).length) parts.push(`${fmt(e.platformManaged.length)} platform-managed row${e.platformManaged.length === 1 ? '' : 's'}`);
  if ((e.hidden || []).length) parts.push(`${fmt(e.hidden.length)} hidden source${e.hidden.length === 1 ? '' : 's'}`);
  if (!parts.length) return '';
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
  return `${list} ${parts.length === 1 && !/s$/.test(parts[0]) ? 'is' : 'are'} not counted.`;
}
